/**
 * The Unusual Space - Shop Theme JavaScript
 * Core functionality for cart, quick add, and utilities
 */

/**
 * Hero slider — Swiper-based, theme-editor friendly.
 * Logic lives here (loaded once, survives the editor's AJAX section re-renders).
 * Swiper is lazy-loaded from CDN only when a hero slider is present on the page.
 */
(function () {
  const SWIPER_VERSION = '11';
  const SWIPER_CSS = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}/swiper-bundle.min.css`;
  const SWIPER_JS = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}/swiper-bundle.min.js`;
  let swiperPromise = null;

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function ensureSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (swiperPromise) return swiperPromise;

    swiperPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-swiper-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = SWIPER_CSS;
        link.setAttribute('data-swiper-css', '');
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = SWIPER_JS;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return swiperPromise;
  }

  function initHeroSlider(root) {
    if (!root) return;

    ensureSwiper()
      .then(() => {
        if (root.swiper) {
          try {
            root.swiper.destroy(true, true);
          } catch (e) {
            /* noop */
          }
        }

        const slideCount = root.querySelectorAll('.swiper-slide').length;
        const multi = slideCount > 1;
        const editing = isDesignMode();
        const delay = Number(root.getAttribute('data-autoplay-delay')) || 5000;

        // eslint-disable-next-line no-new
        new window.Swiper(root, {
          effect: 'fade',
          fadeEffect: { crossFade: true },
          speed: 500,
          loop: multi && !editing,
          autoplay: multi && !editing ? { delay, disableOnInteraction: false } : false,
          pagination: {
            el: root.querySelector('.hero-slider__pagination'),
            clickable: true,
            bulletClass: 'hero-bullet',
            bulletActiveClass: 'hero-bullet--active'
          },
          navigation: {
            nextEl: root.querySelector('.hero-slider__next'),
            prevEl: root.querySelector('.hero-slider__prev')
          },
          a11y: { enabled: true }
        });
      })
      .catch(() => {
        /* CDN failed — static first-slide fallback (CSS) remains visible */
      });
  }

  function initAllHeroSliders(scope) {
    (scope || document).querySelectorAll('[data-hero-swiper]').forEach(initHeroSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllHeroSliders());
  } else {
    initAllHeroSliders();
  }

  // Theme editor: re-init on section reload, clean up on unload, sync on block select.
  document.addEventListener('shopify:section:load', (event) => {
    initAllHeroSliders(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-hero-swiper]');
    if (root && root.swiper) {
      try {
        root.swiper.destroy(true, true);
      } catch (e) {
        /* noop */
      }
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.querySelector('[data-hero-swiper]');
    if (!root || !root.swiper) return;

    const target = root.querySelector(`.swiper-slide[data-block-id="${event.detail.blockId}"]`);
    if (!target) return;

    const slides = Array.from(root.querySelectorAll('.swiper-slide'));
    const index = slides.indexOf(target);
    if (index < 0) return;

    if (root.swiper.autoplay) root.swiper.autoplay.stop();
    root.swiper.slideTo(index);
  });
})();

window.theme = window.theme || {};

window.theme.routes = window.routes || {};

window.theme.sectionsToRender = ['cart-drawer', 'header'];

window.theme.sectionRenderTargets = {
  'cart-drawer': ['#CartDrawer-Header', '#CartDrawer-Contents', '#CartDrawer-Footer'],
  header: ['#cart-icon-bubble']
};

window.theme.getSectionInnerHTML = function (html, selector) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const element = parsed.querySelector(selector);
  return element ? element.innerHTML : '';
};

window.theme.renderSection = function (sectionId, html) {
  if (!html) return;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const sourceSection = parsed.querySelector(`#shopify-section-${sectionId}`);
  if (!sourceSection) return;

  const targets = window.theme.sectionRenderTargets[sectionId];

  if (targets?.length) {
    targets.forEach((selector) => {
      const destination = document.querySelector(`#shopify-section-${sectionId} ${selector}`);
      const source = sourceSection.querySelector(selector);
      if (destination && source) {
        destination.innerHTML = source.innerHTML;
      }
    });
    return;
  }

  const target = document.getElementById(`shopify-section-${sectionId}`);
  if (target) {
    target.innerHTML = sourceSection.innerHTML;
  }
};

window.theme.renderSections = function (sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([sectionId, html]) => {
    window.theme.renderSection(sectionId, html);
  });
};

window.theme.sectionRequestParams = function (extra = {}) {
  return {
    ...extra,
    sections: window.theme.sectionsToRender.join(','),
    sections_url: window.location.pathname
  };
};

window.theme.dispatchCartUpdated = function (cart, openDrawer = false) {
  window.dispatchEvent(new CustomEvent('cart-updated', {
    detail: {
      cart,
      itemCount: cart?.item_count ?? 0,
      openDrawer
    }
  }));
};

window.cartUtils = {
  async addToCart(variantId, quantity = 1, properties = {}, openDrawer = true) {
    try {
      const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(window.theme.sectionRequestParams({
          items: [{ id: variantId, quantity, properties }]
        }))
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const state = await response.json();
      window.theme.renderSections(state.sections);
      window.theme.dispatchCartUpdated(state, openDrawer);
      return state;
    } catch (error) {
      console.error('Error adding to cart:', error);
      const message = window.theme.strings?.cartError || 'Failed to add item to cart. Please try again.';
      alert(message);
      throw error;
    }
  },

  async updateQuantity(key, quantity) {
    try {
      const response = await fetch(`${window.theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(window.theme.sectionRequestParams({
          id: key,
          quantity
        }))
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const state = await response.json();
      window.theme.renderSections(state.sections);
      window.theme.dispatchCartUpdated(state);
      return state;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  },

  async removeItem(key) {
    return this.updateQuantity(key, 0);
  },

  async getCart() {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }
};

document.addEventListener('click', (event) => {
  const qtyButton = event.target.closest('.cart-drawer__qty');
  if (qtyButton) {
    const key = qtyButton.dataset.lineKey;
    const quantity = parseInt(qtyButton.dataset.quantity, 10);
    if (key && !Number.isNaN(quantity)) {
      event.preventDefault();
      if (quantity < 1) {
        window.cartUtils.removeItem(key);
      } else {
        window.cartUtils.updateQuantity(key, quantity);
      }
    }
    return;
  }

  const removeButton = event.target.closest('.cart-drawer__remove');
  if (removeButton) {
    const key = removeButton.dataset.lineKey;
    if (key) {
      event.preventDefault();
      window.cartUtils.removeItem(key);
    }
  }
});

window.quickAddToCart = async function (variantId) {
  try {
    await window.cartUtils.addToCart(variantId, 1);
  } catch (error) {
    console.error('Quick add failed:', error);
  }
};

class ProductForm {
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('[type="submit"]');
    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1, 10);

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    this.submitButton.disabled = true;
    const originalText = this.submitButton.textContent;
    const addingText = window.theme.strings?.adding || 'Adding...';
    this.submitButton.textContent = addingText;

    try {
      await window.cartUtils.addToCart(variantId, quantity, properties);
    } catch (error) {
      // Error already handled by cartUtils
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.textContent = originalText;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const productForms = document.querySelectorAll('form[action*="/cart/add"]');
  productForms.forEach((form) => new ProductForm(form));
});

class VariantSelector {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.currentVariant = options.currentVariant || null;
    this.variants = options.variants || [];
    this.init();
  }

  init() {
    const optionButtons = this.container.querySelectorAll('[data-option-value]');
    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const optionName = button.dataset.optionName;
        const optionValue = button.dataset.optionValue;
        this.selectOption(optionName, optionValue);
      });
    });
  }

  selectOption(optionName, optionValue) {
    const buttons = this.container.querySelectorAll(`[data-option-name="${optionName}"]`);
    buttons.forEach((btn) => {
      btn.classList.toggle('selected', btn.dataset.optionValue === optionValue);
    });

    const selectedOptions = this.getSelectedOptions();
    const matchingVariant = this.findVariant(selectedOptions);

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.updatePrice(matchingVariant);
      this.updateAvailability(matchingVariant);
      this.updateHiddenInput(matchingVariant.id);

      this.container.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: matchingVariant }
      }));
    }
  }

  getSelectedOptions() {
    const options = {};
    const selectedButtons = this.container.querySelectorAll('[data-option-value].selected');
    selectedButtons.forEach((btn) => {
      options[btn.dataset.optionName] = btn.dataset.optionValue;
    });
    return options;
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) =>
      Object.keys(selectedOptions).every((optionName) => {
        const variantOption = variant.options.find((opt) => opt.name === optionName);
        return variantOption && variantOption.value === selectedOptions[optionName];
      })
    );
  }

  updatePrice(variant) {
    const priceEl = this.container.querySelector('[data-variant-price]');
    if (priceEl && variant.price_formatted) {
      priceEl.textContent = variant.price_formatted;
    }
  }

  updateAvailability(variant) {
    const submitButton = this.container.querySelector('[type="submit"]');
    const strings = window.theme.strings || {};
    if (submitButton) {
      submitButton.disabled = !variant.available;
      submitButton.textContent = variant.available
        ? (strings.addToCart || 'Add to cart')
        : (strings.soldOut || 'Sold out');
    }
  }

  updateHiddenInput(variantId) {
    const input = this.container.querySelector('input[name="id"]');
    if (input) {
      input.value = variantId;
    }
  }
}

window.VariantSelector = VariantSelector;

window.addEventListener('scroll', () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollButton = document.querySelector('[data-scroll-to-top]');

  if (scrollButton) {
    if (scrollTop > 500) {
      scrollButton.classList.remove('hidden');
    } else {
      scrollButton.classList.add('hidden');
    }
  }
});

document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener('click', function (e) {
    const href = this.getAttribute('href');
    if (href === '#' || href === '') return;

    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  });
});

document.addEventListener('DOMContentLoaded', async () => {
  try {
    const cart = await window.cartUtils.getCart();
    window.theme.dispatchCartUpdated(cart);
  } catch (error) {
    console.error('Error initializing cart:', error);
  }
});

console.log('The Unusual Space - Shop theme JavaScript loaded');
